import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';

import { documentApi } from '../api';
import { createErrorToast, createSuccessToast } from '../redux/modules/app';
import { queryClient } from '../utils/queryClientUtil';

import insuredKeys from './models/insured/keys';
import recordKeys from './models/record/keys';
import { camelCaseApiResponse, setOverallStatusAndRecordStatusAsFetching } from './util';

// The fetching flag must be stamped before the invalidation: invalidateQueries resolves after the
// refetch, so setting it afterwards leaves it on the fresh data with nothing left to clear it.
const invalidateExpirationStatusQueries = async ({ docId, recordId, insuredId }) => {
  setOverallStatusAndRecordStatusAsFetching(insuredId, recordId);

  await Promise.all([
    queryClient.invalidateQueries(recordKeys.documentExpirations(docId)),
    queryClient.invalidateQueries(recordKeys.recordDocuments(recordId)),
    queryClient.invalidateQueries(recordKeys.recordDocumentRequirements(recordId)),
    queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId)),
    queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId))
  ]);
};

/* QUERIES
========================================================= */
export const useDocumentExpirations = ({ docId, options = {} }) => useQuery(
  recordKeys.documentExpirations(docId),
  () => camelCaseApiResponse(() => documentApi.getDocumentExpirations(docId)),
  options
);

/* MUTATIONS
========================================================= */
export const useCreateDocumentExpiration = ({ docId, recordId, insuredId }) => {
  const dispatch = useDispatch();

  return useMutation(
    data => camelCaseApiResponse(() => documentApi.createDocumentExpiration(docId, data)),
    {
      onError: error => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your custom expiration was not created.',
          error
        }));
      },
      onSuccess: async () => {
        dispatch(createSuccessToast({ title: 'Custom Expiration Added!' }));

        await invalidateExpirationStatusQueries({ docId, recordId, insuredId });
      }
    }
  );
};

export const useCreateDocumentExpirations = ({ docId, recordId, insuredId }) => {
  const dispatch = useDispatch();

  return useMutation(
    expirations => camelCaseApiResponse(() => documentApi.createDocumentExpirations(docId, expirations)),
    {
      onError: error => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'The expiration dates were not saved.',
          error
        }));
      },
      onSuccess: async () => {
        dispatch(createSuccessToast({ title: 'Expiration Dates Saved!' }));

        await invalidateExpirationStatusQueries({ docId, recordId, insuredId });
      }
    }
  );
};

/**
 * Confirms the dates an extraction found. Unlike the bulk create route, this also retires expired
 * links on sibling documents the new document replaces (when the company setting allows).
 */
export const useConfirmExtractedExpirations = ({ gptRequestId, docId, recordId, insuredId }) => {
  const dispatch = useDispatch();

  return useMutation(
    expirations => camelCaseApiResponse(() => documentApi.confirmExtractedExpirations(gptRequestId, expirations)),
    {
      onError: error => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'The expiration dates were not saved.',
          error
        }));
      },
      onSuccess: async () => {
        dispatch(createSuccessToast({ title: 'Expiration Dates Saved!' }));

        await invalidateExpirationStatusQueries({ docId, recordId, insuredId });
      }
    }
  );
};

/**
 * Applies a staged set of changes ({ creates, updates, deletes }) in one go, with a single toast
 * and a single round of invalidations. Creates go through the bulk route; updates and deletes are
 * fanned out per row since no bulk variants exist for them.
 */
export const useSaveDocumentExpirations = ({ docId, recordId, insuredId }) => {
  const dispatch = useDispatch();

  return useMutation(
    ({ creates = [], updates = [], deletes = [] }) => Promise.all([
      ...(creates.length ? [documentApi.createDocumentExpirations(docId, creates)] : []),
      ...updates.map(({ id, data }) => documentApi.updateDocumentExpiration(id, data)),
      ...deletes.map(id => documentApi.deleteDocumentExpiration(id))
    ]),
    {
      onError: error => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Some of your expiration changes were not saved.',
          error
        }));

        return invalidateExpirationStatusQueries({ docId, recordId, insuredId });
      },
      onSuccess: async () => {
        dispatch(createSuccessToast({ title: 'Expiration Dates Saved!' }));

        await invalidateExpirationStatusQueries({ docId, recordId, insuredId });
      }
    }
  );
};

export const useUpdateDocumentExpiration = ({ docId, recordId, insuredId }) => {
  const dispatch = useDispatch();

  return useMutation(
    ({ id, data }) => camelCaseApiResponse(() => documentApi.updateDocumentExpiration(id, data)),
    {
      onError: error => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your custom expiration was not updated.',
          error
        }));
      },
      onSuccess: async () => {
        dispatch(createSuccessToast({ title: 'Custom Expiration Updated!' }));

        await invalidateExpirationStatusQueries({ docId, recordId, insuredId });
      }
    }
  );
};

export const useDeleteDocumentExpiration = ({ docId, recordId, insuredId }) => {
  const dispatch = useDispatch();

  return useMutation(
    id => camelCaseApiResponse(() => documentApi.deleteDocumentExpiration(id)),
    {
      onError: error => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your custom expiration was not deleted.',
          error
        }));
      },
      onSuccess: async () => {
        dispatch(createSuccessToast({ title: 'Custom Expiration Deleted!' }));

        await invalidateExpirationStatusQueries({ docId, recordId, insuredId });
      }
    }
  );
};
